/**
 * Speicher für ungespeicherte Änderungen eines Datensatzes (Story 1.23, EXPERIENCE §4.3): rein, ohne React und Router –
 * `unsaved.tsx` hängt ihn an Router-Blocker, Formulare und die Leiste. Direkt getestet in `test/unsaved-store.test.ts`.
 */

export interface UnsavedEntry {
  dirty: boolean;
  /** Speichern; `null` = Entwurf, der sich nicht speichern lässt (z. B. E-Mail: Senden wäre Außenwirkung). */
  submit: (() => void) | null;
  /** Name der Karte für die Leiste „Nicht gespeichert: Briefkopf, Bank“ (Change 04.10.2026 Formulare kompakt). */
  title?: string;
}

export interface UnsavedStore {
  setEntry: (id: string, entry: UnsavedEntry | null) => void;
  isDirty: () => boolean;
  /** Aktion ausführen, wenn nichts geändert ist; sonst merken und die Leiste zeigen. */
  attempt: (action: () => void) => void;
  /** Leiste sichtbar? */
  prompting: () => boolean;
  subscribe: (listener: () => void) => () => void;
  /** „Verwerfen“: gemerkte Aktion ausführen (Router: Navigation fortsetzen). */
  discard: () => void;
  /** „Speichern“: geänderte Formulare absenden (Entwürfe bleiben stehen), Datensatz bleibt offen. */
  save: () => void;
  /** Mindestens ein geändertes Formular lässt sich speichern (sonst heißt der Knopf „Weiter bearbeiten“). */
  canSave: () => boolean;
  /** Namen der geänderten Formulare, die einen haben (Reihenfolge der Änderung, ohne Doppelte). */
  dirtyTitles: () => string[];
  /** „Weiter bearbeiten“: angehaltene Aktion verwerfen, nichts speichern, nichts verwerfen. */
  keepEditing: () => void;
  /** Router-Blocker (intern). */
  setRouterBlock: (block: { proceed: () => void; reset: () => void } | null) => void;
  /** Ein Verwerfen läuft: der Router-Blocker lässt die folgende Navigation durch. */
  bypassing: () => boolean;
}

export function createUnsavedStore(): UnsavedStore {
  const entries = new Map<string, UnsavedEntry>();
  const listeners = new Set<() => void>();
  let pending: (() => void) | null = null;
  let routerBlock: { proceed: () => void; reset: () => void } | null = null;
  let prompt = false;
  let bypass = false;
  const notify = () => listeners.forEach((l) => l());
  const isDirty = () => [...entries.values()].some((e) => e.dirty);
  const store: UnsavedStore = {
    setEntry(id, entry) {
      const before = entries.get(id)?.dirty ?? false;
      // Neu geändert: ans Ende, damit die Leiste die Karten in der Reihenfolge der Änderung nennt
      // („Nicht gespeichert: Konditionen, Stammdaten“).
      if (entry?.dirty && !before) entries.delete(id);
      if (entry) entries.set(id, entry);
      else entries.delete(id);
      // Neue Eingabe nach einem Verwerfen: wieder schützen.
      if (entry?.dirty && !before) bypass = false;
      // Nichts mehr geändert (z. B. Eingabe zurückgesetzt): Leiste weg, angehaltene Aktion und Router-Halt aufheben.
      if (prompt && !isDirty()) {
        prompt = false;
        pending = null;
        routerBlock?.reset();
        routerBlock = null;
      }
      notify();
    },
    isDirty,
    attempt(action) {
      if (bypass || !isDirty()) {
        action();
        return;
      }
      // Eine neue Aktion ersetzt eine angehaltene Router-Navigation (sonst liefe bei „Verwerfen“ die falsche).
      routerBlock?.reset();
      routerBlock = null;
      pending = action;
      prompt = true;
      notify();
    },
    prompting: () => prompt,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    discard() {
      const action = pending;
      const block = routerBlock;
      pending = null;
      routerBlock = null;
      prompt = false;
      bypass = true;
      notify();
      if (block) block.proceed();
      else action?.();
    },
    save() {
      store.keepEditing();
      for (const e of entries.values()) if (e.dirty) e.submit?.();
    },
    canSave: () => [...entries.values()].some((e) => e.dirty && e.submit !== null),
    dirtyTitles: () => [...new Set([...entries.values()].filter((e) => e.dirty && e.title).map((e) => e.title!))],
    keepEditing() {
      routerBlock?.reset();
      routerBlock = null;
      pending = null;
      prompt = false;
      notify();
    },
    setRouterBlock(block) {
      // Router-Navigation ersetzt eine angehaltene eigene Aktion.
      if (block) pending = null;
      routerBlock = block;
      if (block) prompt = true;
      notify();
    },
    bypassing: () => bypass,
  };
  return store;
}
